import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { useGetAccountLocale } from "@/hooks/useGetAccountLocale";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useTranslation } from "@/hooks/useTranslation";
import { formatTimeAgo } from "@/lib/utils";
import { useGetCounterpartyInfo } from "@/tanstack-hooks";
import { ChevronLeftIcon, UserIcon } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";

interface LocationState {
  from?: string;
}

export default function ConversationHeader({
  counterpartyId,
}: {
  counterpartyId: string;
}) {
  const counterpartyQuery = useGetCounterpartyInfo(counterpartyId, true);
  const navigate = useNavigate();
  const location = useLocation();
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const t = useTranslation();
  const locale = useGetAccountLocale();

  const handleBackClick = () => {
    const state = location.state as LocationState;
    if (state && state.from) {
      navigate(state.from);
    } else {
      navigate(-1);
    }
  };

  function SkeletonHeader(msg?: string) {
    return (
      <div
        className={`flex select-none flex-row w-full justify-between items-center p-2 bg-zinc-900 ${
          isDesktop ? "rounded-tr-2xl" : ""
        }`}
      >
        <div className="w-20 flex justify-start">
          {!isDesktop && (
            <Button
              className="dark pl-1"
              variant="ghost"
              onClick={handleBackClick}
              disabled
            >
              <ChevronLeftIcon className="mr-1 h-4 w-4" />
              {t.SHARED.LABELS.BACK_BUTTON}
            </Button>
          )}
        </div>
        <div className="flex flex-col items-center flex-1 space-y-1">
          <div className="w-32 h-4 bg-zinc-800 rounded-full animate-pulse" />
          {msg ? (
            <span className="text-sm text-zinc-500">{msg}</span>
          ) : (
            <div className="w-24 h-4 bg-zinc-800 rounded-full animate-pulse" />
          )}
        </div>
        <div className="w-20 flex justify-end">
          <div className="w-10 h-10 rounded-full bg-zinc-800 animate-pulse" />
        </div>
      </div>
    );
  }

  if (counterpartyQuery.isLoading) {
    return SkeletonHeader();
  }

  const counterparty = counterpartyQuery.data;

  if (counterpartyQuery.error || !counterparty) {
    return SkeletonHeader(t.PROFILE_VIEW.ERRORS.LOADING);
  }

  return (
    <div
      className={`flex flex-row w-full justify-between items-center p-2 bg-zinc-900 ${
        isDesktop ? "rounded-tr-2xl select-none" : ""
      }`}
    >
      <div className="w-20 flex justify-start">
        {!isDesktop && (
          <Button
            className="dark pl-1"
            variant="ghost"
            onClick={handleBackClick}
          >
            <ChevronLeftIcon className="mr-1 h-4 w-4" />
            {t.SHARED.LABELS.BACK_BUTTON}
          </Button>
        )}
      </div>
      <div className="flex flex-col items-center flex-1">
        <span
          className="font-medium text-sm hover:cursor-pointer"
          onClick={() => navigate("/profile/" + counterparty.counterparty_id)}
        >
          {counterparty.counterparty_name}
          {counterparty.counterparty_age &&
            ", " + counterparty.counterparty_age}
        </span>
        <span className="text-sm text-zinc-500">
          {t.MESSAGES.LABELS.ACTIVE}{" "}
          {formatTimeAgo(counterparty.last_active_at, locale.code)}
        </span>
      </div>
      <div className="w-20 flex justify-end">
        <Avatar
          className="hover:cursor-pointer"
          onClick={() => navigate("/profile/" + counterparty.counterparty_id)}
        >
          <AvatarImage
            src={counterparty.counterparty_photo_url}
            className="object-cover scale-125"
          />
          <AvatarFallback>
            <UserIcon size={20} />
          </AvatarFallback>
        </Avatar>
      </div>
    </div>
  );
}
